import { Link } from "react-router-dom";

import { useLanguage } from "../i18n";
import { useLogger } from "../logger";
import { photoFor } from "../photos";
import { useReasonText } from "../reasons";
import type { Warning } from "../tasksApi";
import type { Specimen } from "../types";

/**
 * A plant somewhere the guide cannot schedule it.
 *
 * This is not a failure to handle quietly. A Species with no interval for its
 * Placement produces no Check task at all, ever — so this card is the only
 * thing on the screen that knows the plant exists. It has to say which plant,
 * where it is, and what to do about it.
 *
 * It is drawn as the room it is in: dark, because that is usually the problem.
 */
export function WrongPlace({
  warnings,
  plants,
}: {
  warnings: Warning[];
  plants: Map<string, Specimen>;
}) {
  const { t, pick } = useLanguage();
  const { log } = useLogger();
  const say = useReasonText();

  if (warnings.length === 0) return null;

  function moveBack(warning: Warning, plant: Specimen | undefined) {
    const to = warning.suggested;
    if (!to || !plant) return;
    log(
      warning.specimen_id,
      "move",
      {
        from: {
          id: plant.location.id,
          ar: plant.location.name.ar,
          en: plant.location.name.en,
          placement: plant.location.placement,
        },
        to: { id: to.id, ar: to.name_ar, en: to.name_en, placement: to.placement },
      },
      `${pick(plant.species.name)} — ${t.movedTo} ${pick({ ar: to.name_ar, en: to.name_en })}`,
    );
  }

  return (
    <section className="not-asking">
      <h2>{t.wrongPlaceTitle}</h2>
      <p className="not-asking-lede">{t.wrongPlaceLede}</p>

      <ul className="warnings">
        {warnings.map((warning) => {
          const plant = plants.get(warning.specimen_id);
          const photo = photoFor(plant?.species.scientific_name);
          return (
            <li key={warning.specimen_id} className="warning">
              <div className="warning-head">
                {photo && <img className="warning-photo" src={photo} alt="" loading="lazy" />}
                <div className="warning-names">
                  <span className="warning-kicker">{t.wrongPlace}</span>
                  <Link to={`/plants/${warning.specimen_id}`} lang="ar" className="warning-plant">
                    {plant?.species.name.ar ?? "—"}
                  </Link>
                  <span className="warning-en" dir="ltr">
                    {plant?.species.name.en}
                  </span>
                </div>
              </div>

              {/* Why the app can say nothing about this plant. The card
                  already shows its name, so the sentence does not repeat it —
                  which also stops an English word order being forced onto the
                  Arabic. */}
              <p className="warning-rule">{say(warning.reason)}</p>

              <div className="chips">
                {/* Only offered when the guide covers somewhere that exists.
                    Acts in place, with the same grace window as any other move
                    — Today does not send you anywhere. */}
                {warning.suggested && (
                  <button
                    type="button"
                    className="chip primary"
                    onClick={() => moveBack(warning, plant)}
                  >
                    {t.moveBackTo}{" "}
                    <span lang="ar">
                      {pick({ ar: warning.suggested.name_ar, en: warning.suggested.name_en })}
                    </span>
                  </button>
                )}

                {/* Always offered, including when there is nowhere to suggest —
                    that is exactly the case where the owner most needs a way to
                    tell the app he knows. The one navigation Today allows:
                    a standing decision made while stationary, not part of the
                    morning loop, and the control already exists there. */}
                <Link className="chip" to={`/plants/${warning.specimen_id}`}>
                  {t.keepItHere}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
